import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Header.module.scss";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HashLink, NavHashLink } from "react-router-hash-link";
import {
  getLayoutHeaderHeight,
  setLayoutValue,
} from "../../features/app/layoutsSlice";
import { getLang, setLang } from "../../features/app/appSlice";
import { getIsSmallScreen } from "../../features/app/appSlice";
import { getCachedProgramsSync, getProgramsAsync, subscribeToProgramsCache } from "../../api/programsCache";
import { ProgramListItem } from "../../admin/types";
import {
  iconFlagCyprus,
  iconFlagElSalvador,
  iconFlagGeorgia,
  iconFlagHungary,
  iconFlagJapan,
  iconFlagNauru,
  iconFlagPanama,
  iconFlagSaoTome,
  nvfoLogoBlue,
  nvfoLogoWhite,
} from "../../assets/imgExport";
import {
  IconAccount,
  IconCharity,
  IconCheckbook,
  IconClose,
  IconCorporate,
  IconEdu,
  IconMenu,
  IconRight,
} from "../../assets/svgExport";
import {
  iconFlagUK,
  iconFlagUAE,
  iconFlagGreece,
  iconFlagTurkey,
  iconFlagMalta,
  iconFlagSpain,
  iconFlagPortugal,
  iconFlagSingapore,
  iconFlagVanuatu,
  iconFlagStKitts,
  iconFlagStLucia,
  iconFlagDominica,
  iconFlagGrenada,
  iconFlagAntigua,
} from "../../assets/imgExport";

function Header(props: any) {
  const [showMenu, setShowMenu] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const lang = useSelector(getLang);
  const isSmallScreen = useSelector(getIsSmallScreen);
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState("");
  const headerHeight = useSelector(getLayoutHeaderHeight);
  const [isHovered, setIsHovered] = useState(false);
  const [whiteBackground, setWhiteBackground] = useState(false);

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location]);

  const [expandedHeaders, setExpandedHeaders] = useState<{
    [key: string]: boolean;
  }>({});
  const [showExpanded, setShowExpanded] = useState(false);
  const navigate = useNavigate();

  // CMS-added programs (admin panel, not in this file's hardcoded lists
  // below) appended into the nav — see mergeCmsNavItems.
  const [cmsPrograms, setCmsPrograms] = useState<ProgramListItem[]>(
    () => getCachedProgramsSync() ?? [],
  );
  useEffect(() => {
    getProgramsAsync()
      .then(setCmsPrograms)
      .catch(() => {});
    // Header mounts once for the whole tab and never re-fetches on its own,
    // so without this it would keep showing whatever was cached at that one
    // call forever -- a program added in the admin panel wouldn't reach the
    // nav until a hard refresh, even though a background revalidation (from
    // this or any other page) already fetched it.
    return subscribeToProgramsCache(setCmsPrograms);
  }, []);

  type NavItem = { to: string; en: string; zh: string; icon: React.ReactNode };

  // Appends any CMS-published program of this category not already covered
  // by the hardcoded list below (matched by route, so a legacy entry always
  // wins) -- new admin-added programs show up here automatically, without a
  // code change.
  function mergeCmsNavItems(category: "residence" | "passport", legacyItems: NavItem[]): NavItem[] {
    const existingPaths = new Set(legacyItems.map((item) => item.to));
    const extra: NavItem[] = cmsPrograms
      .filter((program) => program.programType === category)
      .map((program) => {
        const to = `/program/${program.programType}/${program.slug}`;
        // "headerFlag" specifically -- a separate PNG-only role dedicated to
        // this nav icon (see ImageManager's CORE_SLOTS hint: "网站顶部导航栏
        // 国旗图标"), distinct from the general "flag" role used for the
        // site badge and PDF ("网站徽章 + PDF 国旗标识").
        const flagUrl = program.images.find((img) => img.role === "headerFlag")?.url;
        return {
          to,
          en: program.countryEn,
          zh: program.country,
          icon: flagUrl ? <img src={flagUrl} alt={program.countryEn} /> : <span className="w-5 h-5" />,
        };
      })
      .filter((item) => !existingPaths.has(item.to));
    return [...legacyItems, ...extra];
  }

  const hideMobileHeader = (e: any) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowExpanded(false);
    setIsHovered(false);
    setExpandedHeaders({});
    if (document.querySelector("html")) {
      document.querySelector("html")!.style.overflow = "visible";
    }
  };

  const toggleMenu = (bool: boolean) => {
    if (document.querySelector("html")) {
      document.querySelector("html")!.style.overflow = bool
        ? "hidden"
        : "visible";
    }
    setShowMenu(bool);
    setExpandedHeaders({});
    setShowExpanded(false);
  };

  function ExpandableHeader({
    title,
    headerKey,
  }: {
    title: any;
    headerKey: string;
  }) {
    const isActive =
      currentPath.includes("/" + headerKey) || expandedHeaders[headerKey];
    return (
      <p
        id={`header-${headerKey}`}
        className={`relative flex gap-1 items-center justify-between font-medium hover:cursor-default transition-colors ${
          isSmallScreen
            ? "py-3 border-b border-fourth-light"
            : "h-full px-5 py-2 my-1 after:content-[''] after:absolute after:left-5 after:right-5 after:-bottom-0.5 after:h-[2px] after:bg-gold after:origin-left after:transition-transform after:duration-300"
        } ${
          isSmallScreen
            ? isActive
              ? "text-gold"
              : ""
            : isActive
              ? "after:scale-x-100"
              : "after:scale-x-0 hover:after:scale-x-100"
        }`}
        onMouseEnter={
          isSmallScreen
            ? undefined
            : (e: any) => {
                if (title.zh !== "关于我们") {
                  setExpandedHeaders({
                    program: headerKey === "program",
                    wealth: headerKey === "wealth",
                    legacy: headerKey === "legacy",
                  });
                  setShowExpanded(true);
                }
              }
        }
        onClick={
          !isSmallScreen
            ? undefined
            : (e: any) => {
                if (title.zh !== "关于我们") {
                  setExpandedHeaders({
                    program: headerKey === "program",
                    wealth: headerKey === "wealth",
                    legacy: headerKey === "legacy",
                  });
                  setShowExpanded(true);
                }
              }
        }
      >
        {title.zh}
        {isSmallScreen && (
          <IconRight width="16" height="16" className="opacity-70" />
        )}
      </p>
    );
  }

  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (headerRef.current && headerRef.current.offsetHeight > 0) {
          dispatch(
            setLayoutValue({
              name: "headerHeight",
              value: headerRef.current.offsetHeight,
            }),
          );
        }
      }, 300);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, [dispatch]);

  useEffect(() => {
    const result =
      (isSmallScreen && showMenu) ||
      (!isSmallScreen && showExpanded) ||
      isHovered;
    setWhiteBackground(result);
  }, [isSmallScreen, showMenu, showExpanded, isHovered]);

  function Headers({ isSmallScreen }: { isSmallScreen: boolean }) {
    return (
      <div className={`flex ${isSmallScreen ? "flex-col" : "items-center gap-2"}`}>
        <ExpandableHeader
          title={{
            en: "Program",
            zh: "身份规划",
          }}
          headerKey="program"
        />
        <ExpandableHeader
          title={{
            en: "Wealth Management",
            zh: "财富规划",
          }}
          headerKey="wealth"
        />
        <ExpandableHeader
          title={{
            en: "Legacy Planning",
            zh: "传承规划",
          }}
          headerKey="legacy"
        />
        <HashLink
          id="header-about"
          className={`relative font-medium transition-colors ${
            isSmallScreen
              ? "py-3"
              : "h-full px-5 py-2 my-1 flex items-center after:content-[''] after:absolute after:left-5 after:right-5 after:-bottom-0.5 after:h-[2px] after:bg-gold after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300"
          }`}
          to="/#about-section"
          onMouseEnter={() => {
            !isSmallScreen && setShowExpanded(false);
            setIsHovered(true);
          }}
          onMouseLeave={() => setIsHovered(false)}
          onClick={hideMobileHeader}
        >
          <p className="">{"关于我们"}</p>
        </HashLink>
      </div>
    );
  }
  function CategoryIntro({
    num,
    title,
    description,
    to,
  }: {
    num: string;
    title: string;
    description: string;
    to: string;
  }) {
    return (
      <div
        className={
          isSmallScreen
            ? "w-full pb-6 mb-6 border-b border-fourth-light"
            : "w-[260px] shrink-0 pr-8 border-r border-fourth-light h-full flex flex-col"
        }
      >
        <p className="font-main-serif text-4xl text-gold/50 mb-3">{num}</p>
        <h3 className="text-secondary-dark mb-3">{title}</h3>
        <p className="text-secondary-dark/60 leading-relaxed text-sm mb-6">
          {description}
        </p>
        <HashLink
          to={to}
          onClick={hideMobileHeader}
          className={`${
            isSmallScreen ? "" : "mt-auto"
          } inline-flex items-center gap-1.5 text-gold hover:text-secondary-dark transition-colors text-sm w-fit`}
        >
          了解更多
          <IconRight width={12} height={12} fill="#b89355" />
        </HashLink>
      </div>
    );
  }

  return (
    <>
      <header
        className={`fixed left-0  w-screen z-[9999]  font-medium transition-colors duration-300
          ${
            whiteBackground
              ? "bg-white text-main shadow-[inset_0_-1px_0_0_rgba(184,147,85,0.25)]"
              : "bg-secondary-dark/30 backdrop-blur-sm text-white/80"
          }

        `}
        ref={headerRef}
        onMouseLeave={() => {
          if (isSmallScreen) return;

          setShowExpanded(false);
          setExpandedHeaders({});
        }}
      >
        {/* Top section - Logo and Navigation */}
        <div className={`${isSmallScreen ? "py-3 pl-6 pr-4" : "py-3 pl-14 pr-10"}`}>
          <nav className="flex justify-between items-center gap-8">
            <NavLink
              id="header-logo"
              to="/"
              className="hover:cursor-pointer shrink-0"
              onClick={hideMobileHeader}
            >
              <img
                className={`${
                  isSmallScreen ? "h-6" : "h-8"
                } opacity-90 object-contain`}
                src={whiteBackground ? nvfoLogoBlue : nvfoLogoWhite}
                alt="nvfo-logo"
              />
            </NavLink>

            {!isSmallScreen && (
              <div className="flex items-center gap-8">
                <Headers isSmallScreen={isSmallScreen} />
                <button
                  onClick={() => navigate("/assessment")}
                  className="text-sm font-medium px-6 py-2.5 rounded-full bg-gold text-secondary-dark hover:bg-gold-light transition-colors whitespace-nowrap"
                >
                  移民方案定制
                </button>
              </div>
            )}

            {isSmallScreen && (
              <div onClick={() => toggleMenu(!showMenu)}>
                {showMenu ? (
                  <IconClose height="20" width="20" />
                ) : (
                  <IconMenu height="20" width="20" />
                )}
              </div>
            )}
          </nav>
        </div>

        {/* Expanded section */}
        <div
          className={`${
            isSmallScreen
              ? "flex flex-row justify-between transition-[height]"
              : "grid transition-[grid-template-rows]"
          } duration-300 ease-in-out bg-white border-t border-gold/20 shadow-xl shadow-secondary-dark/5`}
          style={{
            ...(isSmallScreen
              ? { height: showMenu ? "100vh" : "0", overflow: "hidden" }
              : { gridTemplateRows: showExpanded ? "1fr" : "0fr" }),
            visibility:
              (!isSmallScreen && showExpanded) || (isSmallScreen && showMenu)
                ? "visible"
                : "hidden",
          }}
        >
          <div
            className={isSmallScreen ? "contents" : "min-h-0 overflow-hidden"}
          >
            {isSmallScreen && (
              <div className="m-10 text-nowrap">
                <button
                  onClick={() => {
                    hideMobileHeader({ stopPropagation: () => {} });
                    navigate("/assessment");
                  }}
                  className="w-full text-sm font-medium px-6 py-3.5 rounded-full bg-gold text-secondary-dark hover:bg-gold-light transition-colors mb-4"
                >
                  移民方案定制
                </button>
                <Headers isSmallScreen={isSmallScreen} />
              </div>
            )}
            <div
              className={`border-b-[0.5px] border-b-gray-300/30 flex gap-4 ${
                isSmallScreen ? "flex-col h-full" : ""
              }`}
            >
            <div className={`${isSmallScreen ? "" : "shrink-0 pl-14 py-10"}`}>
              {expandedHeaders["program"] && (
                <CategoryIntro
                  num="01"
                  title="身份规划"
                  description="身份规划助您通过多国居留与护照项目实现身份升级，畅享全球化生活与商业机遇。"
                  to="/program"
                />
              )}
              {expandedHeaders["wealth"] && (
                <CategoryIntro
                  num="02"
                  title="财富规划"
                  description="财富规划专注于帮助客户优化全球资产配置，合理利用税务优惠政策，搭建高效商业架构，提升跨境经营能力。"
                  to="/wealth"
                />
              )}
              {expandedHeaders["legacy"] && (
                <CategoryIntro
                  num="03"
                  title="传承规划"
                  description="传承规划通过家族信托、教育支持及慈善公益，确保财富安全传承，同时兼顾社会责任与家族价值延续。"
                  to="/legacy"
                />
              )}
            </div>
            <div
              className={`${
                isSmallScreen
                  ? "overflow-y-scroll pt-6 pb-20"
                  : "p-10 overflow-y-auto"
              }`}
              style={
                !isSmallScreen
                  ? { maxHeight: "calc(100vh - 60px)" }
                  : undefined
              }
            >
              {expandedHeaders["program"] && (
                <div className={isSmallScreen ? "mb-20 p-2" : ""}>
                <div
                  className={`${
                    isSmallScreen
                      ? "flex flex-col gap-10"
                      : "flex gap-16"
                  } `}
                >
                  {["居留项目", "护照项目"].map((desc, index) => (
                    <div
                      key={index}
                      className={`text-secondary-dark font-normal bg-white whitespace-nowrap flex flex-col gap-4`}
                    >
                      <p className="text-secondary-light font-normal text-[12px] font-sans uppercase tracking-wider border-b border-gold/30 pb-2 mb-1">
                        {desc}
                      </p>
                      {mergeCmsNavItems(
                        index === 0 ? "residence" : "passport",
                        [
                        [
                          {
                            to: "/program/residence/uk-innovator",
                            en: "UK Innovator Visa",
                            zh: "英国创新者签证",
                            icon: <img src={iconFlagUK} alt="uk" />,
                          },
                          {
                            to: "/program/residence/uk-global-talent",
                            en: "UK Global Talent Visa",
                            zh: "英国全球人才签证",
                            icon: <img src={iconFlagUK} alt="uk" />,
                          },
                          {
                            to: "/program/residence/uae",
                            en: "UAE Investor Visa",
                            zh: "阿联酋投资移民",
                            icon: <img src={iconFlagUAE} alt="uae" />,
                          },
                          {
                            to: "/program/residence/greece",
                            en: "Greek Golden Visa",
                            zh: "希腊投资移民",
                            icon: <img src={iconFlagGreece} alt="greece" />,
                          },
                          {
                            to: "/program/residence/hungary",
                            en: "Hungary Residence Permit",
                            zh: "匈牙利投资移民",
                            icon: <img src={iconFlagHungary} alt="hungary" />,
                          },
                          {
                            to: "/program/residence/cyprus",
                            en: "Cyprus Residence Permit",
                            zh: "塞浦路斯投资移民",
                            icon: <img src={iconFlagCyprus} alt="cyprus" />,
                          },
                          {
                            to: "/program/residence/portugal",
                            en: "Portuguese Golden Visa",
                            zh: "葡萄牙黄金居留",
                            icon: <img src={iconFlagPortugal} alt="portugal" />,
                          },
                          {
                            to: "/program/residence/panama",
                            en: "Panama Residence Permit",
                            zh: "巴拿马投资移民",
                            icon: <img src={iconFlagPanama} alt="panama" />,
                          },
                          {
                            to: "/program/residence/japan-management",
                            en: "Japan Management Visa",
                            zh: "日本经营管理签证",
                            icon: <img src={iconFlagJapan} alt="japan" />,
                          },
                          {
                            to: "/program/residence/japan-individual",
                            en: "Japan Skilled Individual Visa",
                            zh: "日本高级人才签证",
                            icon: <img src={iconFlagJapan} alt="japan" />,
                          },
                          {
                            to: "/program/residence/malta-residence",
                            en: "Malta Residence Permit",
                            zh: "马耳他投资移民",
                            icon: <img src={iconFlagMalta} alt="malta" />,
                          },
                          {
                            to: "/program/residence/spain",
                            en: "Spanish Investor Visa",
                            zh: "西班牙投资移民",
                            icon: <img src={iconFlagSpain} alt="spain" />,
                          },

                          {
                            to: "/program/residence/singapore",
                            en: "Singapore Work Visa",
                            zh: "新加坡工作签证",
                            icon: (
                              <img src={iconFlagSingapore} alt="singapore" />
                            ),
                          },
                        ],
                        [
                          {
                            to: "/program/passport/malta-passport",
                            en: "Malta Citizenship by Investment",
                            zh: "马耳他护照",
                            icon: <img src={iconFlagMalta} alt="malta" />,
                          },
                          {
                            to: "/program/passport/turkey",
                            en: "Turkey Citizenship by Investment",
                            zh: "土耳其护照",
                            icon: <img src={iconFlagTurkey} alt="turkey" />,
                          },
                          {
                            to: "/program/passport/st-kitts-and-nevis",
                            en: "St Kitts Citizenship by Investment",
                            zh: "圣基茨护照",
                            icon: <img src={iconFlagStKitts} alt="stkitts" />,
                          },
                          {
                            to: "/program/passport/dominica",
                            en: "Dominica Citizenship by Investment",
                            zh: "多米尼克护照",
                            icon: <img src={iconFlagDominica} alt="dominica" />,
                          },
                          {
                            to: "/program/passport/antigua-and-barbuda",
                            en: "Antigua Citizenship by Investment",
                            zh: "安提瓜护照",
                            icon: <img src={iconFlagAntigua} alt="antigua" />,
                          },
                          {
                            to: "/program/passport/grenada",
                            en: "Grenada Citizenship by Investment",
                            zh: "格林纳达护照",
                            icon: <img src={iconFlagGrenada} alt="grenada" />,
                          },
                          {
                            to: "/program/passport/st-lucia",
                            en: "St Lucia Citizenship by Investment",
                            zh: "圣卢西亚护照",
                            icon: <img src={iconFlagStLucia} alt="stlucia" />,
                          },
                          {
                            to: "/program/passport/nauru",
                            en: "Nauru Citizenship by Investment",
                            zh: "瑙鲁护照",
                            icon: <img src={iconFlagNauru} alt="nauru" />,
                          },

                          {
                            to: "/program/passport/georgia",
                            en: "Georgia Citizenship by Investment",
                            zh: "格鲁吉亚护照",
                            icon: <img src={iconFlagGeorgia} alt="georgia" />,
                          },
                          {
                            to: "/program/passport/sao-tome-and-principe",
                            en: "Sao Tome Citizenship by Investment",
                            zh: "圣多美护照",
                            icon: <img src={iconFlagSaoTome} alt="saotome" />,
                          },
                          {
                            to: "/program/passport/vanuatu",
                            en: "Vanuatu Citizenship by Investment",
                            zh: "瓦努阿图护照",
                            icon: <img src={iconFlagVanuatu} alt="vanuatu" />,
                          },
                        ],
                        ][index] ?? [],
                      )?.map((item, itemIndex) => {
                        const isCurrent = currentPath.includes(item.to);
                        return (
                          <NavLink
                            key={itemIndex}
                            to={item.to}
                            onClick={hideMobileHeader}
                            className={`group flex gap-3 items-center -mx-3 px-3 py-2 rounded-lg transition-colors hover:cursor-pointer ${
                              isCurrent
                                ? "bg-gold/10"
                                : "hover:bg-c-gray"
                            }`}
                          >
                            <div className="w-8 shrink-0 flex items-center justify-center [&_img]:h-5 [&_img]:w-auto [&_img]:object-contain">
                              {item.icon}
                            </div>
                            <p
                              className={`hover:cursor-pointer ${
                                isCurrent
                                  ? "text-secondary-dark font-semibold"
                                  : "text-secondary-dark/80 font-normal"
                              }`}
                            >
                              {item.zh}
                            </p>
                          </NavLink>
                        );
                      })}
                    </div>
                  ))}
                </div>
                </div>
              )}
              {expandedHeaders["wealth"] && (
                <div className="w-[280px]">
                  <div className="flex flex-col gap-1">
                    {[
                      {
                        to: "/wealth#tax",
                        zh: "税务规划",
                        desc: "全球资产税务架构与合规优化",
                        icon: <IconAccount fill="#b89355" width="18" height="18" />,
                      },
                      {
                        to: "/wealth#company",
                        zh: "公司服务",
                        desc: "跨境公司注册与财务合规支持",
                        icon: <IconCorporate fill="#b89355" width="18" height="18" />,
                      },
                    ].map((item, itemIndex) => (
                      <HashLink
                        key={itemIndex}
                        to={item.to}
                        onClick={hideMobileHeader}
                        className="group flex gap-4 items-start -mx-3 px-3 py-3 rounded-lg hover:bg-c-gray transition-colors hover:cursor-pointer"
                      >
                        <div className="w-9 h-9 shrink-0 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:border-gold transition-colors">
                          {item.icon}
                        </div>
                        <div>
                          <p className="text-secondary-dark font-medium hover:cursor-pointer">
                            {item.zh}
                          </p>
                          <p className="text-secondary-light text-sm mt-0.5">{item.desc}</p>
                        </div>
                      </HashLink>
                    ))}
                  </div>
                </div>
              )}
              {expandedHeaders["legacy"] && (
                <div className="w-[280px]">
                  <div className="flex flex-col gap-1">
                    {[
                      {
                        to: "/legacy#trust",
                        zh: "家族信托",
                        desc: "跨代资产保护与传承架构",
                        icon: <IconCheckbook fill="#b89355" width="18" height="18" />,
                      },
                      {
                        to: "/legacy#education",
                        zh: "教育规划",
                        desc: "全球教育资源对接与升学辅导",
                        icon: <IconEdu fill="#b89355" width="18" height="18" />,
                      },
                      {
                        to: "/legacy#charity",
                        zh: "慈善公益",
                        desc: "家族慈善与公益项目规划",
                        icon: <IconCharity fill="#b89355" width="18" height="18" />,
                      },
                    ].map((item, itemIndex) => (
                      <HashLink
                        key={itemIndex}
                        to={item.to}
                        onClick={hideMobileHeader}
                        className="group flex gap-4 items-start -mx-3 px-3 py-3 rounded-lg hover:bg-c-gray transition-colors hover:cursor-pointer"
                      >
                        <div className="w-9 h-9 shrink-0 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:border-gold transition-colors">
                          {item.icon}
                        </div>
                        <div>
                          <p className="text-secondary-dark font-medium hover:cursor-pointer">
                            {item.zh}
                          </p>
                          <p className="text-secondary-light text-sm mt-0.5">{item.desc}</p>
                        </div>
                      </HashLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
